import { mapState, mapGetters, mapActions } from 'vuex';
import { cloneDeep, get, isEqual } from 'lodash';

import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { PAGINATION_DEFAULT, SEARCH_PAGE_LIMIT } from '@/constants/pagination';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

import actionItem from '../Partials/ActionItem/ActionItem.vue';
import buildingKitAiTesting from '../Partials/BuildingKitAiTesting/BuildingKitAiTesting.vue';
import MiddlePaneHeader from '../Partials/IQToolMiddlePaneHeader/IQToolMiddlePaneHeader.vue';
import ActionContainer from '../Partials/ActionContainer/ActionContainer.vue';
import aiqCommandBuilderDialog from '../Partials/BuildingKitAiTesting/BuildingKit/Commands/AIQCommandBuilder/AIQCommandBuilderDialog.vue';
import {
  iqtoolsErrorHandler,
  removeActionIfExist,
  removeDuplicateNameAction,
  makeDependencyAlertMsg,
  confirmUnsavedWarning,
  Events,
} from '@/libs';
import { STATUS_CODES } from '@/constants';
import * as log from 'loglevel';
import IqToolsDiagram from '../Diagrams/IqToolsDiagram.vue';
import { getWorkflowDiagramInfo } from '../Diagrams/DiagramHelper';
import {
  IQ_TOOL_ACTION_TYPE_COMMAND,
  IQ_TOOL_ACTION_TYPE_DIALOG,
  IQ_TOOL_ACTION_TYPE_WORKFLOW,
} from '@/constants/iqtool';

import {
  AGENT_LIMIT,
  TEAM_LIMIT,
  TAG_LIMIT,
  CATEGORY_LIMIT,
  ASSETS_LIMIT,
  DOCS_LIMIT,
} from '@/constants/pagination';

export default {
  name: 'workflows',
  components: {
    actionItem,
    buildingKitAiTesting,
    MiddlePaneHeader,
    ActionContainer,
    ManagementPage,
    IqToolsDiagram,
    aiqCommandBuilderDialog,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    const workflowNameRegex = helpers.regex(/^[a-zA-Z_][a-zA-Z.0-9\s_]*$/);
    return {
      newWorkflowName: { required, workflowNameRegex },
    };
  },
  data() {
    return {
      search: '',
      searchPageSize: SEARCH_PAGE_LIMIT,
      loading: true,
      pagination: PAGINATION_DEFAULT,
      showAddWorkflowDialog: false,
      formWorkflow: null,
      newWorkflowName: '',
      nodes: [],
      edges: [],
      commands: [],
      showEditCommandDialog: false,
      selectedCommand: {},
      kitLoaded: false,
      diagramKey: 0,
    };
  },
  computed: {
    ...mapState({
      workflows: state => state.workflows.workflows,
      selectedWorkflow: state => state.workflows.selected,
      useConditionalAction:
        state => state.featureFlags.FEATURE_FLAGS.SUPPORT_CONDITIONAL_ACTION_IN_WORKFLOW,
    }),
    ...mapGetters({
      preconfiguredEntitiesFunc: 'entities/preconfiguredEntities',
      routingProps: 'routing/props',
    }),
    pristine() {
      return isEqual(this.formWorkflow, this.selectedWorkflow);
    },
    middleColumnsClassObj() {
      return {
        pristine: this.pristine,
      };
    },
    preconfiguredEntities() {
      return (this.preconfiguredEntitiesFunc() || []).map(e => e.name);
    },
    isSubmittable() {
      return !this.v$.$invalid;
    },
    isEditMode() {
      return !!(this.selectedWorkflow && this.formWorkflow);
    },
    isViewMode() {
      return !!(this.selectedWorkflow && !this.formWorkflow);
    },
    isListMode() {
      return !this.selectedWorkflow;
    },
    isChanged() {
      return !this.pristine && this.isEditMode;
    },
  },
  async mounted() {
    try {
      await Promise.all([
        this.$store.dispatch('entities/getEntitiesList'),
        this.$store.dispatch('dialogs/getEntityOperators'),
      ]);
    } catch (err) {
      this.$aiq.notify.error(this.$t('iq_tools_tab.workflows.unable_to_load'));
      log.error(err);
    }

    Events.on('navigate.same.route', this.handleReloadPage.bind(this));

    this.onRouteChanged(this.$route);
  },
  beforeUnmount() {
    Events.off('navigate.same.route', this.handleReloadPage.bind(this));
    return this.$store.dispatch('workflows/resetWorkflows');
  },
  watch: {
    $route(to) {
      this.onRouteChanged(to);
    },
  },
  methods: {
    ...mapActions({
      getFunctions: 'buildingKit/getFunctions',
      getDBKAll: 'buildingKit/getDBKAll',
    }),
    loadList({searchTerm, searchOffset}) {

      this.loading = true;

      const params = {
        q: searchTerm,
        limit: this.searchPageSize,
        offset: searchOffset,
      };

      return this.$store.dispatch('workflows/getWorkflowsList', [params, params.offset === 0]).then((response) => {
        this.loading = false;
        this.pagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
      });
    },

    openAddWorkflowDialog() {
      this.showAddWorkflowDialog = true;
    },

    closeAddWorkflowDialog() {
      this.showAddWorkflowDialog = false;
      this.newWorkflowName = '';
    },

    addNewWorkflow() {
      if (!this.newWorkflowName) {
        return;
      }

      const tempWorkflow = { name: this.newWorkflowName };
      return this.$store.dispatch('workflows/createWorkflow', tempWorkflow)
        .then(data => {
          this.$aiq.notify.success(this.$t('iq_tools_tab.workflows.workflow_added'));
          this.newWorkflowName = '';

          // Close dialog
          this.closeAddWorkflowDialog();

          // Redirect to edit page
          return this.editWorkflow(data.data.id, true);
        }, err => {
          iqtoolsErrorHandler(this, err, 'workflow');
        });
    },

    editWorkflow(id, disableSaveWarning) {
      const dispatchSelectWorkflow = () => {
        if (this.selectedWorkflow && this.selectedWorkflow.id === id) {
          return Promise.resolve(this.formWorkflow = cloneDeep(this.selectedWorkflow));
        }

        return this.$store.dispatch('workflows/selectWorkflow', id)
          .then((data) => {
            this.formWorkflow = cloneDeep(data.data);
          })
          .catch(() => {
            this.$aiq.notify.error(this.$t('iq_tools_tab.workflows.not_found'));
            return this.goToListPage();
          });
      };

      if (this.isChanged && this.selectedWorkflow && !disableSaveWarning) {
        const { title, content } = confirmUnsavedWarning;
        return this.$aiq.confirm(title, content)
          .then(dispatchSelectWorkflow);
      }
      return dispatchSelectWorkflow();
    },

    handleReloadPage(pathInfo) {
      if (pathInfo.fullPath === this.$route.fullPath && this.selectedWorkflow) {
        return this.goToListPage();
      }
    },

    goToListPage() {
      if (this.isChanged) {
        const { title, content } = confirmUnsavedWarning;
        return this.$aiq.confirm(title, content)
          .then(() => this.$store.dispatch('workflows/resetSelected')).catch(() => {});
      }

      return this.$store.dispatch('workflows/resetSelected');
    },

    onEditCancel() {
      this.formWorkflow = cloneDeep(this.selectedWorkflow);
    },

    deleteWorkflow(id) {
      const { name } = this.workflows.find(e => e.id === id);
      this.$aiq.confirm(
        this.$t('iq_tools_tab.workflows.delete.title'),
        this.$t('iq_tools_tab.workflows.delete.confirm', { name }),
      ).then(() => {
        return this.$store.dispatch('workflows/deleteWorkflow', id)
          .then(() => {
            this.$aiq.notify.success(this.$t('iq_tools_tab.workflows.delete.workflow_deleted'));
            this.formWorkflow = null;
            return this.goToListPage();
          })
          .catch(res => {
            const { title, content } = makeDependencyAlertMsg('Workflow', res, {$t: this.$t});
            this.$aiq.confirm(title, content);
          });
      });
    },

    viewWorkflow(id = null) {

      if (id === null && this.selectedWorkflow) {
        id = this.selectedWorkflow.id;
      }

      if (id === null) {
        return this.goToListPage();
      }

      const initializeDiagram = () => {
        this.formWorkflow = null;

        if (this.selectedWorkflow) {
          const {nodes, edges, commands} = getWorkflowDiagramInfo(this.selectedWorkflow);
          this.nodes = nodes;
          this.edges = edges;
          this.commands = commands;
        }
      };

      const goToViewPage = () => {
        if (this.selectedWorkflow && id === this.selectedWorkflow.id) {
          return Promise.resolve(initializeDiagram());
        }
        else {
          this.nodes = [];
          this.edges = [];

          return this.$store.dispatch('workflows/selectWorkflow', id)
            .then(() => initializeDiagram())
            .catch(() => {
              this.$aiq.notify.error(this.$t('iq_tools_tab.workflows.not_found'));
              return this.goToListPage();
            });
        }
      };

      if (this.isChanged) {
        const { title, content } = confirmUnsavedWarning;
        return this.$aiq.confirm(title, content)
          .then(goToViewPage)
          .catch(() => {});
      }
      return goToViewPage();
    },

    onEditWorkflow() {
      return this.editWorkflow(this.selectedWorkflow.id, true);
    },

    onCopyWorkflow() {
      return this.$store.dispatch('workflows/copyWorkflow', this.selectedWorkflow.id)
        .then(data => {
          this.$aiq.notify.success(
            this.$t('iq_tools_tab.workflows.copied'),
          );

          return this.editWorkflow(data.id, true);
        }, err => {
          iqtoolsErrorHandler(this, err, 'workflow');
        });
    },

    onDeleteSuccessAction(idx) {
      const actions = this.formWorkflow.payload.success_actions;
      removeActionIfExist(actions, idx);
    },

    onDeleteFailureAction(idx) {
      const actions = this.formWorkflow.payload.failure_actions;
      removeActionIfExist(actions, idx);
    },

    onAddActions(event, actions) {
      removeDuplicateNameAction(
        actions,
        event.newIndex,
        this.formWorkflow,
      );
    },

    onAddFailureActions(event) {
      this.onAddActions(event, this.formWorkflow.payload.failure_actions);
    },

    onAddSuccessActions(event) {
      this.onAddActions(event, this.formWorkflow.payload.success_actions);
    },

    saveFullWorkflow() {
      return this._updateWorkflow()
        .then(newWorkflow => {
          this.$aiq.notify.success(this.$t('iq_tools_tab.workflows.save.success'));
          return this.editWorkflow(newWorkflow.id, true);
        })
        .catch(res => {
          if (res.response.status === STATUS_CODES.CONFLICT) {
            const messagePrefix = 'To finish this workflow, customer\'s ';
            let messageSuffix = 'needed. Do you want to proceed it anyway?';
            let message = '';

            // wrap with style for entities
            let missingEntities = get(res.response, 'data.missing', []);
            missingEntities = missingEntities.map(entity => `<span style="color:#5993FF">${entity}</span>`);

            // build message
            if (missingEntities.length === 1) {
              messageSuffix = ` is ${messageSuffix}`;
              message = messagePrefix + missingEntities + messageSuffix;
            } else {
              messageSuffix = ` are ${messageSuffix}`;
              const commaSeparatedEntities = missingEntities.slice(0, missingEntities.length - 1);

              message = `${messagePrefix + commaSeparatedEntities.join(', ')
              } and ${missingEntities[missingEntities.length - 1]
              }${messageSuffix}`;
            }

            this.$aiq.confirm('Missing entities', message)
              .then(() => this._updateWorkflow(true).then((newWorkflow) => {
                this.$aiq.notify.success(this.$t('iq_tools_tab.workflows.save.success'));
                return this.editWorkflow(newWorkflow.id, true);
              })).catch(() => this.$aiq.notify.error(this.$t('iq_tools_tab.workflows.save.unable_to_save')));
          }
        });
    },

    _updateWorkflow(force = false) {
      return this.$store.dispatch('workflows/updateWorkflow', { payload: this.formWorkflow, force });
    },

    async onActionItemClick(item) {

      if (![IQ_TOOL_ACTION_TYPE_DIALOG, IQ_TOOL_ACTION_TYPE_WORKFLOW].includes(item.unit_type)) {
        return;
      }

      if (this.isChanged && this.selectedWorkflow && this.isEditMode) {
        const { title, content } = confirmUnsavedWarning;
        try {
          await this.$aiq.confirm(title, content);
        } catch (e) {
          // user cancelled
          return;
        }
      }

      if (item.unit_type === IQ_TOOL_ACTION_TYPE_WORKFLOW) {
        return this.editWorkflow(item.id, true);
      }

      return await this.$store.dispatch('routing/routeIqtoolActionItemById', item);
    },

    editWorkflowByName(workflowName) {
      return this.$store.dispatch('workflows/getWorkflowsList', [
        { q: workflowName, offset: 0, limit: 1 },
        true,
      ]).then(response => {
        const searchedWorkflow = response.data?.data;

        if (response?.data?.pagination?.rowCount === 1 && searchedWorkflow && searchedWorkflow.length > 0) {
          return this.editWorkflow(searchedWorkflow[0].id, true);
        }
      });
    },

    onRouteChanged(to) {
      // First check if there's a stored workflow ID from routing action
      const storedWorkflowId = get(this.routingProps, 'workflowId', null);
      if (storedWorkflowId) {
        // Clear the stored ID after using it
        this.$store.dispatch('routing/setProps', { ...this.routingProps, workflowId: null });
        return this.editWorkflow(storedWorkflowId, true);
      }

      // Fall back to hash-based navigation (backward compatibility)
      const workflowName = get(to, 'hash', '').replace('#', '').trim();
      if (workflowName) {
        return this.editWorkflowByName(workflowName);
      }
    },

    onSortByName(a, b) {
      const valA = a.name?.toLowerCase() || '';
      const valB = b.name?.toLowerCase() || '';

      if (valA === valB) {
        return 0;
      }

      return valA < valB ? 1 : -1;
    },

    async loadBuildingKit() {
      if (this.kitLoaded) {
        return Promise.resolve();
      }

      await Promise.all([
        this.$store.dispatch('agents/getAgentsList', [{ limit: AGENT_LIMIT }]),
        this.$store.dispatch('tags/getTagsList', [{ limit: TAG_LIMIT }]),
        this.$store.dispatch('categories/getCategoriesList', [{ limit: CATEGORY_LIMIT }]),
        this.$store.dispatch('teams/getTeamsList', [{ limit: TEAM_LIMIT }]),
        this.$store.dispatch('assets/getAssetsList', [{ limit: ASSETS_LIMIT }]),
        this.$store.dispatch('documents/getDocumentsList', [{ limit: DOCS_LIMIT }]),
      ]);

      this.getFunctions(this.$store);
      this.kitLoaded = true;
    },

    async handleEditNode({type, id, name}) {
      const dialogAction = { id, name, unit_type: IQ_TOOL_ACTION_TYPE_DIALOG };

      id = Number(id);
      switch (type) {
        case IQ_TOOL_ACTION_TYPE_COMMAND:
          this.selectedCommand = (this.commands || []).find(c => c.id === id);
          if (this.selectedCommand) {
            await this.loadBuildingKit();
            this.showEditCommandDialog = true;
          }
          break;

        case IQ_TOOL_ACTION_TYPE_DIALOG:
          await this.onActionItemClick(dialogAction);
          break;

        case IQ_TOOL_ACTION_TYPE_WORKFLOW:
          this.editWorkflow(id, true);
          break;
      }
    },

    handleCommandUpdated() {
      if (this.selectedWorkflow && this.selectedWorkflow.id) {
        return this.$store.dispatch('workflows/selectWorkflow', this.selectedWorkflow.id)
          .then(() => this.viewWorkflow(this.selectedWorkflow.id))
          .then(() => {
            // Force remount of the workflow diagram
            this.diagramKey += 1;
          })
          .catch(() => {
            this.$aiq.notify.error(this.$t('iq_tools_tab.workflows.not_found'));
            return this.goToListPage();
          });
      }
      return Promise.resolve();
    },
  },
};
